import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FloppyDisk, ArrowCounterClockwise, Image as ImageIcon } from "@phosphor-icons/react";

const EMPTY = {
  logo_url: "",
  app_name: "",
  app_tagline: "",
  signin_kicker: "",
  signin_title: "",
  signin_subtitle: "",
  hero_image_url: "",
  hero_kicker: "",
  hero_title: "",
  hero_bullets: [],
  footer_text: "",
};

export default function BrandingPage() {
  const [form, setForm] = useState(EMPTY);
  const [initial, setInitial] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/branding").then((r) => {
      const d = { ...EMPTY, ...(r.data || {}) };
      d.hero_bullets = Array.isArray(d.hero_bullets) ? d.hero_bullets : [];
      setForm(d);
      setInitial(d);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"));
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        hero_bullets: (form.hero_bullets || []).map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/branding", payload);
      toast.success("Login page branding updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => setForm(initial);

  const bulletsText = (form.hero_bullets || []).join("\n");

  return (
    <div className="space-y-6" data-testid="branding-page">
      <PageHeader
        title="Login Page Branding"
        eyebrow="Superuser"
        subtitle="Customize the logo, hero image and text that everyone sees on the login screen — applies app-wide."
        testid="branding-header"
        actions={(
          <>
            <Button type="button" variant="outline" onClick={reset} disabled={busy} data-testid="branding-reset"
              className="rounded-sm border-[var(--hms-border-2)] bg-[var(--hms-paper)]">
              <ArrowCounterClockwise size={14} weight="bold" className="mr-1.5" /> Reset
            </Button>
            <Button type="submit" form="branding-form" disabled={busy} data-testid="branding-save"
              className="rounded-sm bg-[var(--hms-accent)] hover:bg-[var(--hms-accent)]/90">
              <FloppyDisk size={14} weight="bold" className="mr-1.5" /> {busy ? "Saving…" : "Save changes"}
            </Button>
          </>
        )}
      />

      <form id="branding-form" onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — Brand mark + sign-in copy */}
        <section className="hms-card p-5 space-y-4">
          <div className="hms-section-title">Brand mark & sign-in copy</div>

          <UrlField
            label="Logo image URL"
            value={form.logo_url}
            onChange={(v) => set("logo_url", v)}
            testid="branding-logo-url"
            placeholder="https://… (leave blank to use the default hospital icon)"
            preview
          />

          <Field label="App name" testid="branding-app-name">
            <Input value={form.app_name} onChange={(e) => set("app_name", e.target.value)}
              data-testid="branding-app-name" placeholder="MEDIRA"
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>

          <Field label="App tagline (under the name)" testid="branding-app-tagline">
            <Input value={form.app_tagline} onChange={(e) => set("app_tagline", e.target.value)}
              data-testid="branding-app-tagline" placeholder="Hospital Management · IPD / OPD"
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>

          <div className="border-t border-[var(--hms-border)] pt-3" />

          <Field label="Sign-in kicker (small text above the headline)" testid="branding-signin-kicker">
            <Input value={form.signin_kicker} onChange={(e) => set("signin_kicker", e.target.value)}
              data-testid="branding-signin-kicker" placeholder="Sign in"
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>

          <Field label="Sign-in headline" testid="branding-signin-title">
            <Input value={form.signin_title} onChange={(e) => set("signin_title", e.target.value)}
              data-testid="branding-signin-title" placeholder="Care, charted with clarity."
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>

          <Field label="Sign-in sub-text paragraph" testid="branding-signin-subtitle">
            <Textarea rows={3} value={form.signin_subtitle} onChange={(e) => set("signin_subtitle", e.target.value)}
              data-testid="branding-signin-subtitle"
              placeholder="One workspace for OPD, IPD, billing…"
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>

          <Field label="Footer line" testid="branding-footer">
            <Input value={form.footer_text} onChange={(e) => set("footer_text", e.target.value)}
              data-testid="branding-footer-text" placeholder="© Medira HMS"
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>
        </section>

        {/* RIGHT — Hero panel */}
        <section className="hms-card p-5 space-y-4">
          <div className="hms-section-title">Right-side hero panel</div>

          <UrlField
            label="Hero image URL"
            value={form.hero_image_url}
            onChange={(v) => set("hero_image_url", v)}
            testid="branding-hero-image-url"
            placeholder="https://images.unsplash.com/…"
            preview
            previewClass="h-48"
          />

          <Field label="Hero kicker (uppercase eyebrow)" testid="branding-hero-kicker">
            <Input value={form.hero_kicker} onChange={(e) => set("hero_kicker", e.target.value)}
              data-testid="branding-hero-kicker" placeholder="Built for clinicians"
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>

          <Field label="Hero headline" testid="branding-hero-title">
            <Textarea rows={2} value={form.hero_title} onChange={(e) => set("hero_title", e.target.value)}
              data-testid="branding-hero-title"
              placeholder="From admit card to discharge — one trail, zero paperwork."
              className="rounded-sm border-[var(--hms-border-2)] bg-white" />
          </Field>

          <Field label="Bullet list (one per line)" testid="branding-hero-bullets">
            <Textarea rows={6} value={bulletsText}
              onChange={(e) => set("hero_bullets", e.target.value.split("\n"))}
              data-testid="branding-hero-bullets"
              placeholder={"Multi-hospital, multi-role · Superuser, Admin, User\nOPD ↔ IPD conversion with persistent MRN\n…"}
              className="rounded-sm border-[var(--hms-border-2)] bg-white font-mono text-xs" />
            <div className="text-[11px] text-[var(--hms-muted)] mt-1">
              {(form.hero_bullets || []).filter((b) => b.trim()).length} bullet(s)
            </div>
          </Field>
        </section>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function UrlField({ label, value, onChange, testid, placeholder, preview = false, previewClass = "h-20" }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder}
        className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white text-xs font-mono" />
      {preview && (
        <div className={`mt-2 w-full ${previewClass} border border-dashed border-[var(--hms-border-2)] rounded-sm bg-[var(--hms-paper)] overflow-hidden flex items-center justify-center`}>
          {value ? (
            <img src={value} alt="preview" className={`${previewClass} w-full object-cover`} data-testid={`${testid}-preview`}
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="flex items-center gap-2 text-[var(--hms-muted)] text-xs">
              <ImageIcon size={16} weight="bold" /> No image — falls back to default
            </div>
          )}
        </div>
      )}
    </div>
  );
}
